// "Lift above the finger" for touch dragging in the interior editor.
//
// A finger covers what it drags (the "fat finger" occlusion), and the hand comes
// from below, so everything under and around the touch point is hidden. While a
// finger drags furniture, the item rides ABOVE the touch point instead, so the
// player always sees the item and the landing zone drawn under it.
//
// Everything here is in SCREEN (CSS) pixels: the lift is about the size of a
// finger, not of the room, so it must look the same at min and max zoom and on
// any screen. A mouse pointer hides nothing — no lift for it.

/** Clearance between the touch point and the item's bottom edge (≈ half a fingertip). */
export const TOUCH_LIFT_CLEARANCE_PX = 36;
/** Never lift further than this: a big item grabbed high stays reachable and readable. */
export const TOUCH_LIFT_MAX_PX = 110;
/**
 * Holding a finger on an item this long picks it up (it starts rising above the
 * finger) even before it moves — a tap is shorter, so taps never lift.
 */
export const TOUCH_HOLD_PICKUP_MS = 150;
/**
 * How long the item takes to rise above the finger. Long enough to read as being
 * lifted, not as a jump; the landing zone and the red/green check move with it.
 * Time-based, not travel-based: a lift that grew with finger travel made the item
 * drift UP while the finger moved down.
 */
export const TOUCH_LIFT_RISE_MS = 320;

/**
 * 0 → 1 over TOUCH_LIFT_RISE_MS since the pick-up. Ease-in-out: it starts and
 * ends gently — an ease-out curve moves fastest in the very first frame, which is
 * exactly what reads as a jerk.
 */
export function touchLiftEase(elapsedMs: number): number {
  const t = Math.max(0, Math.min(1, elapsedMs / TOUCH_LIFT_RISE_MS));
  // Sine ease-in-out: the evenest speed of the classic in-out curves (peak ≈1.6×
  // the average), so no single frame moves the item much.
  return (1 - Math.cos(Math.PI * t)) / 2;
}

/**
 * How far (screen px) to raise the dragged item above where the finger holds it:
 * just enough that the item's bottom edge sits TOUCH_LIFT_CLEARANCE_PX above the
 * finger, capped at TOUCH_LIFT_MAX_PX. Zero for a mouse, and for an item that is
 * already entirely above the finger.
 */
export function computeTouchLift(pointerType: string | undefined, touchScreenY: number, itemBottomScreenY: number): number {
  if (pointerType !== 'touch' && pointerType !== 'pen') return 0;
  const needed = itemBottomScreenY - touchScreenY + TOUCH_LIFT_CLEARANCE_PX;
  return Math.max(0, Math.min(TOUCH_LIFT_MAX_PX, needed));
}
